import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useStore } from "store/zustand";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import { onAuthStateChanged } from "firebase/auth";
import { doc, deleteDoc, getDocFromServer } from "firebase/firestore";
import {
  applyReferralApi,
  CreateAccountApi,
  updateAccountApi,
  uploadFileApi,
  verifyUploadApi,
} from "apis/authServices";
import { auth, firestore } from "config/firebaseConfig";
import {
  clearSignupDraft,
  readSignupDraft,
  SignupAccountPayload,
  SKIP_IMAGE_VERIFICATION,
} from "./signupDraft";
import RegisterBg from "../../assets/images/Register.png";
import StandbyIcon from "../../assets/images/standby.png";
import SelfieImg from "../../assets/images/Selfie.png";
import IdCardImg from "../../assets/images/id-card.png";
import ScheduleImg from "../../assets/images/schedule.png";
import AppUploadImg from "../../assets/images/app-upload.png";

interface ProofOption {
  key: string;
  image: string;
  text: string;
}

// The accepted kinds of proof, one per carousel page.
const PROOF_OPTIONS: ProofOption[] = [
  {
    key: "selfie",
    image: SelfieImg,
    text: "A selfie in uniform",
  },
  {
    key: "id",
    image: IdCardImg,
    text: "A picture of your airline retiree card",
  },
  {
    key: "schedule",
    image: ScheduleImg,
    text: "A picture of your schedule with your name clearly visible",
  },
  {
    key: "app",
    image: AppUploadImg,
    text: "A screenshot of your company's non-rev website or app",
  },
];

// How long each proof example stays on screen before the carousel advances.
const AUTO_ADVANCE_MS = 3500;

// Upload cap advertised under the dropzone — keep the two in sync, and in sync
// with the mobile app's UploadProof screen.
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
const MAX_UPLOAD_LABEL = "5MB";

const UploadProof: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setIsAuthenticated } = useStore();
  const { showLoader, hideLoader, setLoaderMessage } = useGlobalLoader();

  // Router state is the primary source — captured once so it stays stable
  // across renders. The draft step 2 stashed is the refresh fallback.
  const [stateAccountPayload] = useState<SignupAccountPayload | null>(
    () =>
      (location.state as { accountPayload?: SignupAccountPayload })
        ?.accountPayload ?? null,
  );
  const [accountPayload, setAccountPayload] =
    useState<SignupAccountPayload | null>(stateAccountPayload);
  // Firebase reports the signed-in user asynchronously, so the "no payload"
  // redirect below has to wait for it — otherwise a refresh bounces to step 2
  // before the draft has had a chance to load.
  const [authResolved, setAuthResolved] = useState(false);

  useEffect(() => {
    return onAuthStateChanged(auth, (user) => {
      setAuthResolved(true);
      if (stateAccountPayload || !user) return;
      const draft = readSignupDraft(user.uid);
      if (draft) setAccountPayload(draft.accountPayload);
    });
  }, [stateAccountPayload]);

  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== "undefined" ? window.innerWidth < 1024 : false,
  );
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [uploadError, setUploadError] = useState<string>("");
  const [isDragging, setIsDragging] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  // Set while the pointer is over the carousel, so auto-advance never moves the
  // slide the user is reading.
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);

  // In QA and qaproduction builds image verification is disabled. If this page
  // is somehow reached (e.g. a stale URL or a direct visit), send the user
  // straight back to step 2 so they are never blocked here.
  useEffect(() => {
    if (SKIP_IMAGE_VERIFICATION) {
      navigate("/createAccount", { replace: true });
    }
  }, [navigate]);

  // Reached only through step 2 — a direct visit has no profile data to
  // persist, so send them back to fill the form in.
  useEffect(() => {
    if (authResolved && !accountPayload) {
      navigate("/createAccount", { replace: true });
    }
  }, [authResolved, accountPayload, navigate]);

  // Object URLs for the preview have to be released by hand.
  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl("");
      return;
    }
    const url = URL.createObjectURL(selectedFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  // Picking a photo hides the carousel; reset so it and the dots agree when it
  // comes back (a rejected photo brings it straight back).
  useEffect(() => {
    if (selectedFile) setActiveIndex(0);
  }, [selectedFile]);

  // Cycles the proof examples on a timer. Re-arms off `activeIndex`, so a manual
  // swipe / dot tap just restarts the countdown from wherever the user landed.
  useEffect(() => {
    if (paused || selectedFile) return;
    const timer = setTimeout(
      () => setActiveIndex((i) => (i + 1) % PROOF_OPTIONS.length),
      AUTO_ADVANCE_MS,
    );
    return () => clearTimeout(timer);
  }, [activeIndex, paused, selectedFile]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    setPaused(true);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const startX = touchStartX.current;
    touchStartX.current = null;
    setPaused(false);
    if (startX === null) return;
    const deltaX = e.changedTouches[0].clientX - startX;
    if (Math.abs(deltaX) < 40) return; // ignore taps / tiny drags
    setActiveIndex((i) =>
      deltaX < 0
        ? (i + 1) % PROOF_OPTIONS.length
        : (i - 1 + PROOF_OPTIONS.length) % PROOF_OPTIONS.length,
    );
  };

  const validateFile = (file: File): boolean => {
    const allowedFormats = ["image/jpeg", "image/png"];
    if (!allowedFormats.includes(file.type)) {
      setUploadError("Only JPG and PNG files are allowed.");
      return false;
    }

    if (file.size > MAX_UPLOAD_BYTES) {
      const sizeInMb = (file.size / (1024 * 1024)).toFixed(1);
      setUploadError(
        `That image is ${sizeInMb}MB. Please choose one under ${MAX_UPLOAD_LABEL}.`,
      );
      return false;
    }

    return true;
  };

  // Polls the AI result Cloud Function writes for this user.
  const fetchVerificationStatusFromFirestore = async (
    userId: string,
    startTimeMs: number,
  ): Promise<{ message: string; isAirlineStaff: boolean }> => {
    const MAX_TIMEOUT = 90_000;
    const POLL_INTERVAL = 3_000;
    const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

    const userDocRef = doc(firestore, "imageProcessingStatus", userId);
    const deadline = Date.now() + MAX_TIMEOUT;

    while (Date.now() < deadline) {
      try {
        const docSnap = await getDocFromServer(userDocRef);

        if (!docSnap.exists()) {
          // Doc not created yet — keep polling.
        } else {
          const data = docSnap.data();
          const updatedAtDate: Date | null = data?.updatedAt?.toDate
            ? data.updatedAt.toDate()
            : null;
          const updatedAtMs = updatedAtDate ? updatedAtDate.getTime() : 0;

          // Skip stale docs from before this upload
          if (updatedAtMs > 0 && updatedAtMs < startTimeMs - 5000) {
            // Stale doc from a previous upload — keep polling.
          } else if (
            data?.queueStatus === "completed" &&
            typeof data?.isAirlineStaff === "boolean"
          ) {
            return {
              message: data?.description || "Verification status received.",
              isAirlineStaff: data.isAirlineStaff,
            };
          }
        }
      } catch (e: any) {
        // Transient read error — keep polling until the deadline.
      }

      await wait(POLL_INTERVAL);
    }

    throw new Error(
      "Image verification is taking longer than expected. Please try again or upload a different image.",
    );
  };

  // Writes the step 2 profile data. On a resumed signup the backend record may
  // not exist yet (CreateAccount never ran / failed), so UpdateAccount 404s with
  // "User not found" — create the record, then retry the update.
  const persistAccount = useCallback(async () => {
    if (!accountPayload) return;
    const write = () =>
      updateAccountApi(
        accountPayload.firstName,
        accountPayload.lastName,
        accountPayload.employerAirlineId,
        accountPayload.homebaseId,
        accountPayload.referralCode ?? "",
      );

    try {
      await write();
    } catch (err: any) {
      const status = err?.response?.status;
      const serverMsg =
        typeof err?.response?.data === "string"
          ? err.response.data
          : err?.response?.data?.message;
      const userMissing =
        status === 404 || /user not found/i.test(serverMsg || "");
      if (userMissing) {
        await CreateAccountApi();
        await write();
      } else {
        throw err;
      }
    }
  }, [accountPayload]);

  const verifyAndCreateAccount = useCallback(
    async (file: File) => {
      setBusy(true);
      setUploadError("");
      // The AI check can run for a while, so the loader narrates each stage — a
      // bare spinner on a 30s+ wait reads as a frozen page.
      showLoader("Uploading your photo...");
      try {
        const user = auth.currentUser;
        if (!user) {
          throw new Error("User not authenticated. Please log in.");
        }
        const uid = user.uid;

        // Upload + AI check. Kept in its own try so a failure here is reported
        // as a verification problem, not as "couldn't create account".
        let verificationResult: { message: string; isAirlineStaff: boolean };
        try {
          const uploadStartTimeMs = Date.now();

          // Try to delete stale doc (best effort — may fail due to security rules)
          try {
            await deleteDoc(doc(firestore, "imageProcessingStatus", uid));
          } catch (e) {
            // Best effort — ignore failures (e.g. blocked by security rules).
          }

          const uploadResponse = await uploadFileApi(file);
          if (!uploadResponse || typeof uploadResponse !== "object") {
            throw new Error("Invalid or undefined upload response");
          }

          const { fileUrl, userId } = uploadResponse;
          if (
            fileUrl === undefined ||
            fileUrl === null ||
            userId === undefined ||
            userId === null
          ) {
            throw new Error("Missing fileUrl or userId in upload response");
          }

          setLoaderMessage(
            "Verifying your airline credentials...\nThis can take up to a minute. Please keep this page open.",
          );

          const verifyResponse = await verifyUploadApi(fileUrl, userId);
          if (!verifyResponse?.message) {
            throw new Error("Invalid verification response");
          }

          verificationResult = await fetchVerificationStatusFromFirestore(
            uid,
            uploadStartTimeMs,
          );
        } catch (error: any) {
          console.error("Error verifying image:", error);
          setSelectedFile(null);
          const apiMessage =
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.response?.data?.title;
          setUploadError(
            apiMessage ||
              error?.message ||
              "Image verification failed. Please try again.",
          );
          return;
        }

        const { message, isAirlineStaff } = verificationResult;

        if (isAirlineStaff !== true) {
          // AI could not confirm the person is airline/airport staff — reject it
          // and let them try another photo.
          setSelectedFile(null);
          const detail = (message || "").trim();
          setUploadError(
            detail
              ? `We couldn't verify your image: ${detail.charAt(0).toUpperCase() + detail.slice(1)} Please upload a clear photo showing you in airline/airport staff uniform or ID.`
              : "We couldn't verify your image. Please upload a clear photo showing you in airline/airport staff uniform or ID.",
          );
          return;
        }

        // Verified — now (and only now) persist the account details from step 2.
        try {
          setLoaderMessage("Verified! Setting up your account...");
          await persistAccount();
        } catch (error: any) {
          console.error("Error updating account:", error);
          setSelectedFile(null);
          const apiMessage =
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.response?.data?.title;
          setUploadError(
            apiMessage ||
              "We couldn't complete your account. Please try uploading again.",
          );
          return;
        }

        // Apply referral credit if a referral code was supplied
        const referralCode = accountPayload?.referralCode?.trim();
        if (referralCode) {
          try {
            await applyReferralApi(referralCode);
          } catch (referralError: any) {
            console.error("Failed to apply referral:", referralError);
            // Don't block signup — the account already exists.
            // Only surface the "already applied" case on the home page.
            const message: string = referralError?.message || "";
            if (message.toLowerCase().includes("already")) {
              sessionStorage.setItem("referralMessage", message);
            }
          }
        }

        clearSignupDraft();
        setIsAuthenticated(true);
        navigate("/");
      } finally {
        hideLoader();
        setBusy(false);
      }
    },
    [
      accountPayload,
      hideLoader,
      navigate,
      persistAccount,
      setIsAuthenticated,
      setLoaderMessage,
      showLoader,
    ],
  );

  const handleFile = (file: File | null) => {
    if (!file) return;
    setUploadError("");
    if (!validateFile(file)) {
      setSelectedFile(null);
      return;
    }
    setSelectedFile(file);
    verifyAndCreateAccount(file);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFile(event.target.files?.[0] || null);
    // Allow re-picking the same file after a rejection.
    event.target.value = "";
  };

  const handleDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    if (busy) return;
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (busy) return;
    handleFile(event.dataTransfer.files[0] || null);
  };

  const handleCancelImage = () => {
    setSelectedFile(null);
    setUploadError("");
  };

  if (!accountPayload) return null;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 50 }}>
      {/* Full-page Background Image */}
      <img
        src={RegisterBg}
        alt="Register background"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.35)",
        }}
      />

      {/* Content Layer */}
      <div
        style={{
          position: "relative",
          zIndex: 10,
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          width: "100%",
          height: "100%",
          overflowY: isMobile ? "auto" : "hidden",
        }}
      >
        {/* Left - Logo & Tagline */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            padding: isMobile ? "1.5rem" : "2.5rem",
          }}
        >
          <div className="flex items-center gap-2">
            <img src={StandbyIcon} alt="Standby" className="w-8 h-8" />
            <span className="text-white text-xl font-bold tracking-wide">
              STANDBY
            </span>
          </div>
          {!isMobile && (
            <div className="mb-10">
              <h1 className="text-white text-4xl font-bold leading-tight">
                Connecting <span className="text-light-yellow">Air Crews,</span>
                <br />
                <span className="italic">Seamlessly.</span>
              </h1>
            </div>
          )}
        </div>

        {/* Right - Form Card */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: isMobile ? "1rem" : "2rem",
            minWidth: isMobile ? 0 : "640px",
            maxWidth: isMobile ? "100%" : "720px",
            width: isMobile ? "100%" : undefined,
          }}
        >
          <div
            className="w-full"
            style={{
              maxWidth: isMobile ? "28rem" : undefined,
              marginLeft: isMobile ? "auto" : undefined,
              marginRight: isMobile ? "auto" : undefined,
            }}
          >
            <div
              className={
                isMobile
                  ? "bg-white rounded-2xl shadow-lg px-6 py-10 overflow-y-auto"
                  : "bg-white rounded-2xl shadow-lg px-12 py-12 overflow-y-auto"
              }
              style={{ maxHeight: isMobile ? "none" : "95vh" }}
            >
              <div className="flex justify-center">
                <span className="inline-block px-4 py-1 rounded-full border border-gray-200 text-xs text-grease-100">
                  Step 3 of 3
                </span>
              </div>
              <h2 className="text-2xl font-bold text-grease-100 text-center mt-4">
                Confirm your eligibility
              </h2>
              <p className="text-sm mt-2 font-normal text-grease-50 text-center mx-auto max-w-xs">
                Verify your airline employment to activate your account.
              </p>

              {selectedFile && previewUrl ? (
                <div className="mt-6 flex justify-center">
                  <div className="relative">
                    <img
                      src={previewUrl}
                      alt="Selected proof"
                      className="w-48 h-48 object-cover rounded-xl border border-gray-200"
                    />
                    {!busy && (
                      <button
                        type="button"
                        onClick={handleCancelImage}
                        aria-label="Remove selected image"
                        className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white text-sm flex items-center justify-center hover:bg-black/75"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm font-medium text-grease-100 text-center mt-6 mb-3">
                    Submit one of the following:
                  </p>
                  <div
                    className="overflow-hidden"
                    onMouseEnter={() => setPaused(true)}
                    onMouseLeave={() => setPaused(false)}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                  >
                    <div
                      className="flex transition-transform duration-500 ease-in-out"
                      style={{
                        transform: `translateX(-${activeIndex * 100}%)`,
                      }}
                    >
                      {PROOF_OPTIONS.map((option) => (
                        <div
                          key={option.key}
                          className="w-full flex-shrink-0 flex justify-center px-1"
                        >
                          <div
                            className="bg-[#FEF6E0] rounded-2xl py-5 px-6 flex flex-col items-center w-full"
                            style={{ maxWidth: isMobile ? "100%" : "72%" }}
                          >
                            <img
                              src={option.image}
                              alt=""
                              className="w-24 h-24 object-contain mb-3"
                            />
                            <p className="text-sm font-medium text-grease-100 text-center leading-5">
                              {option.text}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-center gap-1.5 mt-4 mb-5">
                    {PROOF_OPTIONS.map((option, index) => (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => setActiveIndex(index)}
                        aria-label={`Show proof example ${index + 1}`}
                        className={`h-[7px] rounded-full transition-all duration-300 ${
                          index === activeIndex
                            ? "w-5 bg-[#192227]"
                            : "w-[7px] bg-[#EADFC4]"
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}

              <div className="w-full mt-5">
                <label
                  htmlFor="dropzone-file"
                  className={`flex w-full px-6 py-5 border-2 border-light-yellow border-dashed rounded-lg ${
                    busy
                      ? "opacity-60 cursor-not-allowed"
                      : "cursor-pointer hover:bg-yellow-50"
                  } ${isDragging ? "bg-yellow-50" : ""} ${
                    isMobile
                      ? "flex-col items-center gap-3"
                      : "flex-row items-center justify-between"
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
                    <img className="w-7" src="/images/upload.svg" alt="" />
                    <p className="text-xs text-grease-100 text-center truncate max-w-full">
                      {busy
                        ? "Verifying your photo..."
                        : selectedFile
                          ? selectedFile.name
                          : "Drag your file(s) to start uploading"}
                    </p>
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0">
                    {/* The vertical "or" rule only reads correctly beside the
                        dropzone text; the stacked mobile layout drops it. */}
                    {!isMobile && (
                      <div className="flex flex-col items-center">
                        <div className="w-px h-4 bg-gray-200" />
                        <span className="text-gray-400 text-xs my-1">or</span>
                        <div className="w-px h-4 bg-gray-200" />
                      </div>
                    )}
                    <button
                      type="button"
                      disabled={busy}
                      className="font-semibold px-4 py-2 rounded-lg text-grease-100 text-center border border-gray-300 text-xs hover:bg-gray-50 bg-white disabled:cursor-not-allowed"
                      onClick={(e) => {
                        e.preventDefault();
                        if (busy) return;
                        document.getElementById("dropzone-file")?.click();
                      }}
                    >
                      Browse files
                    </button>
                  </div>
                  <input
                    id="dropzone-file"
                    type="file"
                    className="hidden"
                    disabled={busy}
                    onChange={handleFileChange}
                    accept="image/jpeg,image/jpg,image/png"
                  />
                </label>
              </div>

              {uploadError && (
                <p className="mt-3 text-sm text-red-700 bg-red-50 p-3 rounded-lg">
                  {uploadError}
                </p>
              )}

              <p className="text-xs text-gray-400 text-center mt-4">
                JPG or PNG . Max Size {MAX_UPLOAD_LABEL}
              </p>

              {/* An explicit target, not navigate(-1): after a refresh the
                  previous history entry may not be step 2 at all. */}
              <button
                type="button"
                onClick={() => navigate("/createAccount")}
                disabled={busy}
                className="w-full text-sm font-medium text-grease-200 text-center mt-5 disabled:opacity-50 disabled:cursor-not-allowed hover:underline"
              >
                Back
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadProof;
